import { AxiosResponse } from 'axios';
import { camelizeKeys } from 'humps';
import { isFunction } from 'lodash';
import { normalize, Schema } from 'normalizr';
import { put } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';

import { EntityBase } from '../resources/types';

import fetchLookups, { sagaFetchLookups } from './fetchLookups';
import { PayloadApiRequest, PayloadApiSuccess } from './sagaApi';
import showMessage from './showMessage';
import updateLoading from './updateLoading';

// TODO: Unpack type in case of array
// type Unpack<A> = A extends Array<infer E> ? E : A;

/**
 * Defines lookups per resource entity
 *
 * @example
 *
 * includeLookups: {
 *   resourceKey1: {
 *     subResourceKey1: {},
 *     subResourceKey2: {},
 *   },
 *   resourceKey2: {},
 * },
 */
type DeepLookups = any;
// TODO: Support validation for 2+ levels deep
// type DeepLookups<E> = {
//   [K in keyof E]?: DeepPartial<Unpack<E[K]>>;
// };

export interface MetaHandleApiResponse<E> {
  callbackSuccess?: (response: PayloadApiSuccess<E>) => void;
  camelizeResponseKeys?: boolean;
  includeLookups?: DeepLookups;
  resolveLookups?: boolean;
  messageSuccess?: string | ((response: PayloadApiSuccess<E>) => string);
  normalize?: (data: any, schema?: Schema, response?: AxiosResponse) => PayloadApiSuccess<E>['normalized'];
  schema?: Schema;
  skipMessages?: boolean;
}

export default function* sagaHandleApiResponse<E extends EntityBase>(
  res: AxiosResponse & { normalized?: PayloadApiSuccess<E>['normalized'] },
  payload: PayloadApiRequest<E>,
) {
  const { action, resource, meta = {} } = payload;

  // updateLoading false
  if (meta.showLoading) {
    yield put(updateLoading(false));
  }

  // Call succeed > handle `meta`
  let data = res.data || {};

  // Camelize response keys
  if (meta.camelizeResponseKeys) {
    // prevent conversion of keys containing only uppercase letters or numbers
    // https://github.com/domchristie/humps#converting-object-keys
    data = camelizeKeys(data, (key: string, convert: any) => (/^[A-Z0-9_]+$/.test(key) ? key : convert(key)));
  }

  // Normalize response
  let normalized;
  if (res.normalized) {
    normalized = res.normalized;
  } else if (meta.normalize) {
    normalized = meta.normalize(data, meta.schema, res);
  } else if (meta.schema) {
    normalized = normalize(data, meta.schema);
  } else {
    normalized = { entities: {} };
  }
  // TODO: Skip sending `meta` as we already have te `payload.meta`
  const response = { ...res, data, normalized, meta, config: payload } as PayloadApiSuccess<E>;

  // Dispatching success before fetching lookups if `resolveLookups: false`,
  // so lookups don't try to fetch again something we already have on `response.normalized`
  if (action && !meta.resolveLookups) {
    yield put(action.success(response));
  }

  // Fetch lookups before dispatching `success` cos we might want to resolve lookups using `resolveLookups: true`
  if (meta.includeLookups && resource) {
    yield sagaFetchLookups({
      type: getType(fetchLookups),
      payload: { ...response, resource },
    });
  }

  // Dispatch `success` action
  if (action && meta.resolveLookups) {
    yield put(action.success(response));
  }

  // Show message success
  if (!meta.skipMessages && meta.messageSuccess) {
    const messageSuccess = isFunction(meta.messageSuccess) ? meta.messageSuccess(response) : meta.messageSuccess;
    if (messageSuccess) {
      yield put(showMessage(messageSuccess, 'success'));
    }
  }

  // Callback success
  if (meta.callbackSuccess) {
    meta.callbackSuccess(response);
  }
  return response;
}
